import { useCallback, useEffect, useRef } from 'react';

import { GameStatus } from '@/features/letterParty/types';

import type { Dispatch, SetStateAction } from 'react';
import type { Position } from '@/features/letterParty/types';

interface ApplyDamageParams {
	currentSnake: Position[];
	damagedSnake: Position[];
	playHitSound: () => void;
}

interface UseLetterPartyDamageParams {
	setGameStatus: Dispatch<SetStateAction<GameStatus>>;
	setIsRecovering: Dispatch<SetStateAction<boolean>>;
	setIsStopped: Dispatch<SetStateAction<boolean>>;
	setVibeLevel: Dispatch<SetStateAction<number>>;
}

const DAMAGE_RECOVERY_DELAY_MS = 1000;
const VIBE_DAMAGE_PENALTY = 30;
const MINIMUM_VIBE_AFTER_DAMAGE = 5;

export function useLetterPartyDamage({
	setGameStatus,
	setIsRecovering,
	setIsStopped,
	setVibeLevel,
}: UseLetterPartyDamageParams) {
	const recoveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearRecoveryTimeout = useCallback(() => {
		if (!recoveryTimeoutRef.current) {
			return;
		}

		clearTimeout(recoveryTimeoutRef.current);
		recoveryTimeoutRef.current = null;
	}, []);

	const applyDamage = useCallback(
		({ currentSnake, damagedSnake, playHitSound }: ApplyDamageParams) => {
			if (damagedSnake.length <= 1) {
				setGameStatus(GameStatus.GameOver);
				return currentSnake;
			}

			playHitSound();
			setIsRecovering(true);
			clearRecoveryTimeout();
			recoveryTimeoutRef.current = setTimeout(() => {
				setIsRecovering(false);
				setIsStopped(true);
				recoveryTimeoutRef.current = null;
			}, DAMAGE_RECOVERY_DELAY_MS);

			setVibeLevel((prev) => {
				const nextLevel = prev - VIBE_DAMAGE_PENALTY;
				return nextLevel <= 0 ? MINIMUM_VIBE_AFTER_DAMAGE : nextLevel;
			});

			return damagedSnake;
		},
		[clearRecoveryTimeout, setGameStatus, setIsRecovering, setIsStopped, setVibeLevel],
	);

	useEffect(() => clearRecoveryTimeout, [clearRecoveryTimeout]);

	return {
		applyDamage,
	};
}
